// apps/share-web/src/text-share/wake-lock.ts
//
// Экран не гаснет посреди песни.
//
// Без этого фича бесполезна ровно там, где она нужна: телефон на стойке гасит
// экран через 30 с бездействия, а музыкант его не трогает — за него листает
// лидер. Screen Wake Lock просит браузер держать экран, пока страница видима.
//
// ТРИ ЗАМЕРЕННЫХ СВОЙСТВА API, каждое здесь учтено:
//
//  1. Требуется secure context, и без взаимодействия с документом запрос могут
//     отказать (NotAllowedError). Поэтому просит его ПОВЕРХНОСТЬ, в момент,
//     когда чтение началось, — а не этот модуль по своей инициативе при
//     загрузке. «Началось» это и касание, и автоподключение к лайву: там отказ
//     возможен, но не гарантирован (телефон, которого уже касались, отдаёт
//     замок), а стоит попытка ничего — см. пункт 3.
//  2. Браузер отпускает замок САМ, когда вкладка уходит в фон, и обратно его не
//     возвращает. Отсюда `refresh()` на возврате: без него экран гаснет после
//     первого же переключения приложения.
//  3. API есть не везде. Десктопный Firefox и happy-dom не знают его вовсе — и
//     это НЕ ошибка читателя: сообщать ему нечего, просить его нечего, лист от
//     этого не перестаёт вестись. Все промахи глотаются молча, ни одной строки
//     в консоль: страница на стойке не место для диагностики.

/** Sentinel по спеке: держит замок и знает, отпущен ли он. */
export interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<unknown>;
}

export interface WakeLockApiLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

export interface WakeLockSeam {
  /** По жесту: «хочу держать экран». Промах — не ошибка. */
  request(): void;
  /** Возврат из фона: браузер отпустил замок сам, берём снова — если хотели. */
  refresh(): void;
  /** Отпустить и перестать хотеть. */
  release(): void;
}

/** `navigator.wakeLock`, если он вообще есть. Спрашивается через `typeof` —
 *  тем же приёмом, что matchMedia в phone-mode.ts. */
function defaultApi(): WakeLockApiLike | null {
  if (typeof navigator === 'undefined') return null;
  const api = (navigator as unknown as { wakeLock?: WakeLockApiLike }).wakeLock;
  return api && typeof api.request === 'function' ? api : null;
}

export function createWakeLock(api: WakeLockApiLike | null = defaultApi()): WakeLockSeam {
  let wanted = false;
  let held: WakeLockSentinelLike | null = null;

  /** Держим ли мы замок ПРЯМО СЕЙЧАС — с поправкой на пункт 2 шапки. */
  const holding = (): boolean => held !== null && held.released !== true;

  const acquire = (): void => {
    if (!api || holding()) return;
    held = null;
    // Через promise-цепочку, а не try/catch: `request` бросает и синхронно
    // (нет secure context), и отказом промиса (нет жеста) — оба глотаются
    // одинаково молча.
    void Promise.resolve()
      .then(() => api.request('screen'))
      .then((sentinel) => {
        // Пока запрос летел, читатель мог отключиться. Тогда замок нам больше
        // не нужен — и оставить его висеть значило бы держать экран телефона,
        // которого никто не просил.
        if (wanted) held = sentinel;
        else void Promise.resolve().then(() => sentinel.release()).catch(() => {});
      })
      .catch(() => {});
  };

  return {
    request(): void {
      wanted = true;
      acquire();
    },
    refresh(): void {
      if (wanted) acquire();
    },
    release(): void {
      wanted = false;
      const sentinel = held;
      held = null;
      if (sentinel) void Promise.resolve().then(() => sentinel.release()).catch(() => {});
    },
  };
}
